import type { FollowUpRemindersTypes } from '@/shared/lib/types/accountProfile';
import type { AccountType, ActivityLogType, CcmAutoEnrollmentFields, RelyingPartyAdminType, UserType } from '@/shared/lib/types/graphql';
import type Meta from '@/shared/lib/types/meta';
import type { AccountMetric, PatientsData } from '@/shared/lib/types/patients';
import type {
  AccountDevicesType,
  AccountMedicalServiceEnrollmentSummary,
  AccountNoteType,
  AccountOrderType,
  AccountSmartGoalType,
  AccountsMonitoringDeviceType,
  ActivityLog,
  AlertLimitsType,
  AlertLimitType,
  CareActivityType,
  ConsentType,
  Diagnosis,
  EnrolledAccountsFilterType,
  PatientReviewNoteType,
  PatientReviewTemplateType,
  PatientReviewWorkflowType,
  ProviderType,
  ProviderWorkqueueItemType,
  ProviderWorkqueueProviderType,
  ProviderWorkqueueSummaryType,
  RPMAccountAlert,
  RpmAccountsType,
  RpmAlertsCount,
  RpmClaimsType,
  RpmFollowupConsultType,
  RpmProviderActivitiesType,
  RpmReportsCommunicationType,
  RpmReportsType,
  SmartGoalCategory,
  TableReadingsType,
  UpdateRpmReportStatusTypeEnum,
  VitalsDataAvailabilityType
} from '@/shared/lib/types/remotePatientMonitoring';
import type { MetaDataType } from '@/shared/lib/types/tasks';
import type { JSONData } from '@/shared/ui/JsonKit';
import { gql } from '@apollo/client';

export type GetAccountDevicesResponse = {
  getMonitoringDevices: {
    monitoringDevices: AccountDevicesType[];
  };
};

export enum GetAccountVitalsReadingsErrors {
  NO_DATA_READINGS = 'No patient readings found'
}

export type ReadingsSummaryType = {
  uniqueDaysCount: string;
  criticallyHigh: number;
  high: number;
  normal: number;
  low: number;
  criticallyLow: number;
};

export type ReadingsSummaryResponseType = {
  uniqueDaysCount: number;
  uniqueDaysWithBaselineCount: number;
  hasBaseline: boolean;
  criticallyHigh: number;
  high: number;
  normal: number;
  low: number;
  criticallyLow: number;
};

export type GetAccountVitalsReadingsResponse = {
  getAccountVitalsReadings: {
    range?: string;
    average?: string;
    unit?: string;
    xValues?: string[];
    yValues?: number[];
    readingsSummary?: ReadingsSummaryResponseType;
    tableReadings: [TableReadingsType];
    meta?: MetaDataType;
  };
};

export type GetRpmAlertsResponse = {
  getRpmAlerts: {
    rpmAlerts: RPMAccountAlert[];
    meta: MetaDataType;
  };
};

export type GetRpmAlertsCountResponse = {
  getRpmAlertsCounts: RpmAlertsCount;
};

export type GetRpmProviderApprovalResponse = {
  getProviderApprovalAccounts: {
    accountsCounts: {
      readyForReviewCount: number;
      scheduleForReviewCount: number;
      inReviewCount: number;
      approvedCount: number;
      skippedCount: number;
    };
    accounts: PatientsData[];
    meta: Meta;
  };
};

export type RpmEnrolledAccounts = {
  account: PatientsData;
  devices: null | JSONData;
  lastContactedType: null | string;
  lastContactedDate: null | string;
  monthlyTotalReadings: number;
  monthlyTimeSpent: number;
  rpmMonthlyTimeSpent?: number | null;
  ccmMonthlyTimeSpent?: number | null;
  lastLogBpAt: string | null;
  lastLogBgAt: string | null;
  lastLogHrAt: string | null;
  lastFollowupVisitOn: string | null;
  lastCompletedFollowupVisitOn: string | null;
  followupVisitDueStatus: string | null;
  isLastVisitAsync: boolean;
  isRpmReportAvailable: boolean;
  readingBasedCategoryName: string;
  activityBasedCategoryName: string;
  lastActivityAt: string | null;
  lastActivityDoneBy: RelyingPartyAdminType | null;
};

export type GetRpmEnrolledAccountsResponse = {
  getRpmEnrolledAccounts: {
    rpmEnrolledAccounts: RpmEnrolledAccounts[];
    meta: Meta;
    snapshotsRefreshedAt?: string | null;
    lastFullRefreshAt?: string | null;
  };
};

export type GetAccountsByRpmStatusResponse = {
  getAccountsByRpmStatus: { accounts: RpmAccountsType[]; meta: Meta };
};

export type GetPriorirityAccountsResponse = {
  getAccountsByRpmStatus: { accounts: RpmAccountsType[]; meta: Meta };
};

export type GetRpmProviderAccountsResponse = {
  getProviderSessionAccounts: {
    providerActivities: RpmProviderActivitiesType[];
    meta: Meta;
  };
};

export type CareSummaryUrlType = {
  url: string;
  requestedOn: string;
};

export type GetRpmProviderSessionAccountResponse = {
  getProviderSessionAccount: {
    account: PatientsData;
    summary: string | null;
    careSummaryUrls: CareSummaryUrlType[];
    rpmEncounterUrl: string | null;
  };
};

export type CarePlanValues = {
  conditions: string[];
  outcomes: string[];
  goals: string[];
  barriers: string[];
};

export type CarePlanByAccount = {
  programDescription: string;
  consent: ConsentType;
} & CarePlanValues;

export type GetCarePlanByAccountResponse = {
  getCarePlanByAccount: CarePlanByAccount;
};
export type GetCarePlanValuesResponse = {
  getCarePlanValues: CarePlanValues;
};

export type ConstDataType = {
  id: string;
  accountId: string;
  rpmParticipation: boolean;
  ccmParticipation: boolean;
  rpmShareData: boolean;
  jointCollaborationWithIndycare: boolean;
  rpmMonthlyReportSharing: boolean;
  rpmWillReturnDevicesOnCancellation: boolean;
  rpmCanCancelAnytime: boolean;
  rpmObtainedOn: string;
  rpmObtainedBy: RelyingPartyAdminType;
};

export type GetRpmConsentResponse = {
  getRpmConsent: ConstDataType;
};

export type GetUpdateRpmConsentResponse = {
  updateRpmConsent: {
    consent: ConstDataType;
  };
};

export type GetAccountConfigurationsTypes = {
  getAccountConfigurations: {
    id: string;
    rpmReportProviderByFax: boolean;
    rpmReportProviderByEmail: boolean;
    rpmReportProviderByText: boolean;
    rpmReportPatientByEmail: boolean;
    rpmReportPatientByText: boolean;
    account: AccountType;
    provider: ProviderType;
  }[];
};

export type GetActivityLogsResponse = {
  activityLogsByFilter: {
    activityLogs: ActivityLog[];
    meta: { totalCount: number; totalPages: number };
  };
};

export type GetAccountLastNoteResponse = {
  getAccountLastNote: {
    lastNoteDate: string;
  };
};

export type GetAccountNotesByFilterResponse = {
  getAccountNotesByFilter: AccountNoteType[];
};

export type GetAccountNotesResponse = {
  getAccountNotes: {
    notes: AccountNoteType[];
    meta: Meta;
  };
};

export type GetAccountNotesByIdResponse = {
  getAccountNotesById: AccountNoteType;
};

export type UpdateRpmAlertsResponse = {
  updateRpmAlerts: {
    rpmAlerts: RPMAccountAlert[];
    status: string;
    errors: string[];
  };
};

export type GetRpmReviewActivitiesResponse = {
  getRpmReviewActivities: {
    careActivity: CareActivityType[];
    meta: Meta;
  };
};

export type GetRpmActivitiesResponse = {
  getRpmActivities: {
    careActivity: CareActivityType[];
    setupTotalTime: number;
    monthlyTotalTime: number;
    meta: Meta;
  };
};

export type GetAccountVitalsDataAvailabilityResponse = {
  getAccountVitalsDataAvailability: VitalsDataAvailabilityType;
};

export const GET_ACCOUNT_DEVICES = gql`
  query GetMonitoringDevicesQuery {
    getMonitoringDevices {
      monitoringDevices {
        id
        name
        brandName
        typeName
      }
    }
  }
`;

export const GET_RPM_CANDIDATE_STATISTICS = gql`
  query GetRpmCandidatesStatisticsQuery {
    getRpmCandidatesStatistics {
      statistics {
        title
        value
      }
    }
  }
`;

export const GET_ACCOUNT_VITALS_READINGS_FOR_PAGE = gql`
  query GetAccountVitalsReadingsQuery($pageSize: Int!, $pageNum: Int!, $truentityId: String!, $rpmData: RpmInput!) {
    getAccountVitalsReadings(pageSize: $pageSize, pageNum: $pageNum, truentityId: $truentityId, rpmData: $rpmData) {
      range
      average
      unit
      readingsSummary {
        uniqueDaysCount
        uniqueDaysWithBaselineCount
        hasBaseline
        criticallyHigh
        high
        normal
        low
        criticallyLow
      }
      tableReadings {
        id
        source
        value
        status
        recordedAt
        isBaseline
        alert {
          id
          label
          description
          shortDescription
          reading
          isRead
          readAt
          recordedAt
          immediateActions
          lastViewedAt
          lastViewedBy
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_ACCOUNT_VITALS_READINGS_FOR_TABLE = gql`
  query GetAccountVitalsReadingsQuery($pageSize: Int!, $pageNum: Int!, $truentityId: String!, $rpmData: RpmInput!) {
    getAccountVitalsReadings(pageSize: $pageSize, pageNum: $pageNum, truentityId: $truentityId, rpmData: $rpmData) {
      tableReadings {
        id
        source
        value
        status
        recordedAt
        isBaseline
        alert {
          id
          label
          description
          shortDescription
          reading
          isRead
          readAt
          recordedAt
          immediateActions
          lastViewedAt
          lastViewedBy
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_RPM_PROVIDER_APPROVAL_ACCOUNTS = gql`
  query GetProviderApprovalAccountsQuery($pageNum: Int!, $pageSize: Int!, $filterOptions: AccountFilterOptionsInput!) {
    getProviderApprovalAccounts(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accountsCounts {
        scheduleForReviewCount
        approvedCount
        inReviewCount
        skippedCount
        enrolledCount
      }
      accounts {
        id
        truentityId
        phone
        gender
        totalTimeSpentSecsInCurrentMonth
        rpmStatus
        rpmStatusUpdatedAt
        rpmApprovalStatus
        rpmSignOffStatus
        onboardedAt
        healthPlan {
          orgName
        }
        birthDate
        zipcode
        rpmStatusComment
        rpmEnrolledAt
        dateOfLastReview
        user {
          firstName
          lastName
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_PRIORITY_ACCOUNTS = gql`
  query GetAccountsByRpmStatusQuery($pageNum: Int, $pageSize: Int, $filterOptions: AccountFilterOptionsInput!) {
    getAccountsByRpmStatus(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accounts {
        id
        truentityId
        phone
        gender
        onboardedAt
        rpmStatus
        ccmStatus
        clinicalCareEncounterMode
        healthPlan {
          orgName
        }
        accountMetric {
          rpmPriorityScore
          medicationData
          claimsData
          isSnoozed
          medsyncData
          diagnosesData
          hypertensionLikelihoodScore
          diabetesLikelihoodScore
          metaData
        }
        birthDate
        zipcode
        rpmEnrolledAt
        rpmUnenrolledAt
        lastMedrecDate
        rpmApprovalStatus
        rpmSignOffStatus
        setting
        doNotCall
        user {
          firstName
          lastName
          verifiedEmail
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_RPM_ACCOUNTS_BY_RPM_STATUS = gql`
  query GetAccountsByRpmStatusQuery($pageNum: Int, $pageSize: Int, $filterOptions: AccountFilterOptionsInput!) {
    getAccountsByRpmStatus(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accounts {
        id
        truentityId
        phone
        gender
        rpmEnrolledAt
        rpmUnenrolledAt
        lastMedrecDate
        rpmApprovalStatus
        rpmSignOffStatus
        onboardedAt
        rpmStatus
        ccmStatus
        clinicalCareEncounterMode
        healthPlan {
          orgName
        }
        birthDate
        zipcode
        setting
        doNotCall
        user {
          firstName
          lastName
          verifiedEmail
        }
        accountsAssignments {
          relyingPartyAdmin {
            id
            name
          }
        }
        accountsFollowupReminders {
          id
          type
          mode
          appointmentRemindersEnabled
          followUpOn
          relyingPartyAdmin {
            id
          }
        }
        tags {
          id
          name
          colorCode
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

/** Shared by the grid queries and the live-update subscription so pushed rows match grid rows. */
export const RPM_SNAPSHOT_ROW_FIELDS = gql`
  fragment RpmSnapshotRowFields on RpmSnapshotsAccount {
    account {
      truentityId
      user {
        firstName
        lastName
      }
      healthPlan {
        id
        orgName
      }
      rpmEnrolledAt
      rpmUnenrolledAt
      lastMedrecDate
      rpmStatus
      ccmStatus
      setting
      rpmApprovalStatus
      rpmSignOffStatus
      tags {
        id
        name
        colorCode
      }
      phone
      birthDate
      zipcode
      billingHoldActive
      activeBillingHoldReasons
      visibleBillingHolds {
        reason
        label
        detail
        severity
        startedAt
        billingMonth
      }
      eligibilityReview {
        status
        reason
        payer
        lastChecked
        noInsurance
      }
      smsOptOut {
        optedOutAt
        keyword
        source
      }
    }
    devices
    lastContactedType
    lastContactedDate
    monthlyTotalReadings
    monthlyTimeSpent
    rpmMonthlyTimeSpent
    ccmMonthlyTimeSpent
    lastLogBpAt
    lastLogBgAt
    lastLogHrAt
    lastCompletedFollowupVisitOn
    followupVisitDueStatus
    isLastVisitAsync
    isRpmReportAvailable
    readingBasedCategoryName
    activityBasedCategoryName
    lastActivityAt
    lastActivityDoneBy {
      user {
        firstName
        lastName
      }
    }
  }
`;

export const GET_RPM_ENROLLED_ACCOUNTS = gql`
  query GetRpmEnrolledAccountsQuery(
    $pageNum: Int!
    $pageSize: Int!
    $filterMonthYear: String!
    $filterByAccountIds: [ID!]
    $filterType: String
    $sortModel: [SortModelInput!]
    $tagFilters: [ID!]
    $filterByPaused: Boolean
    $filterByBillingWarning: Boolean
    $filterByEligibilityUnverified: Boolean
    $filterBySmsOptedOut: Boolean
  ) {
    getRpmEnrolledAccounts(
      pageNum: $pageNum
      pageSize: $pageSize
      filterMonthYear: $filterMonthYear
      filterType: $filterType
      filterByAccountIds: $filterByAccountIds
      sortModel: $sortModel
      tagFilters: $tagFilters
      filterByPaused: $filterByPaused
      filterByBillingWarning: $filterByBillingWarning
      filterByEligibilityUnverified: $filterByEligibilityUnverified
      filterBySmsOptedOut: $filterBySmsOptedOut
    ) {
      rpmEnrolledAccounts {
        ...RpmSnapshotRowFields
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
  ${RPM_SNAPSHOT_ROW_FIELDS}
`;

export type RpmSnapshotUpdatedResponse = {
  rpmSnapshotUpdated: {
    snapshot: RpmEnrolledAccounts | null;
    pharmacyRefreshed: boolean;
  };
};

export const ON_RPM_SNAPSHOT_UPDATED = gql`
  subscription OnRpmSnapshotUpdated($truentityIds: [ID!]!) {
    rpmSnapshotUpdated(truentityIds: $truentityIds) {
      pharmacyRefreshed
      snapshot {
        ...RpmSnapshotRowFields
      }
    }
  }
  ${RPM_SNAPSHOT_ROW_FIELDS}
`;

export type UpdateCandidateStatusResponse = {
  updateCandidateStatus: {
    status: string;
    message: string;
  };
};

export const UPDATE_RPM_CANDIDATE_STATUS = gql`
  mutation UpdateCandidateStatus($truentityIds: [ID!]!, $rpmStatus: String!, $ccmStatus: String, $reason: String) {
    updateCandidateStatus(truentityIds: $truentityIds, rpmStatus: $rpmStatus, ccmStatus: $ccmStatus, reason: $reason) {
      status
      message
    }
  }
`;

/**
 * Records that the current admin opened an alert in the review panel.
 *
 * Deliberately separate from marking it read: read means the workflow was submitted, viewed means
 * somebody looked. The gap between them is what shows an alert was picked up and abandoned.
 */
export const LOG_RPM_ALERT_VIEWED = gql`
  mutation LogRpmAlertViewed($alertId: String!) {
    logRpmAlertViewed(alertId: $alertId) {
      status
      message
    }
  }
`;

export const GET_RPM_ALERTS_COUNT = gql`
  query GetRpmAlertsCountsQuery($truentityId: ID, $filterOptions: RpmAccountAlertsFilterOptionsInput!) {
    getRpmAlertsCounts(truentityId: $truentityId, filterOptions: $filterOptions) {
      criticallyHigh
      criticallyLow
      high
      low
      rpmSetup
      noReadings
      medRegimen
      reportFaxFailed
    }
  }
`;

export const GET_RPM_ALERTS = gql`
  query GetRpmAlertsQuery(
    $truentityId: ID
    $pageNum: Int!
    $pageSize: Int!
    $alertGenericType: RpmAlertGenericTypeEnum
    $filterOptions: RpmAccountAlertsFilterOptionsInput!
    $sortModel: [SortModelInput!]
  ) {
    getRpmAlerts(
      truentityId: $truentityId
      pageNum: $pageNum
      pageSize: $pageSize
      alertGenericType: $alertGenericType
      filterOptions: $filterOptions
      sortModel: $sortModel
    ) {
      rpmAlerts {
        id
        account {
          id
          truentityId
          user {
            firstName
            lastName
          }
          birthDate
          zipcode
          phone
          rpmEnrolledAt
          rpmStatus
          accountSummary {
            noReadingsReminderSentAt
          }
          tags {
            id
            name
            colorCode
          }
        }
        isRead
        reading
        description
        shortDescription
        label
        alertType
        careActivity {
          id
          title
          notes
          subType
          type
          subTitle
          isEncounter
          totalTimeSpentSecs
          contactMode
          readingType: vitalsType
          startedAt
          endedAt
          updatedAt
        }
        lastReadingAt
        createdAt
        recordedAt
        immediateActions
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const UPDATE_RPM_ALERTS = gql`
  mutation UpdateRpmAlerts($alertIds: [ID!]!, $input: UpdateRpmAlertInput!) {
    updateRpmAlerts(alertIds: $alertIds, input: $input) {
      rpmAlerts {
        id
        isRead
      }
      status
      errors
    }
  }
`;

export type ProviderSessionType = {
  id?: string;
  sessionId: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateProviderSessionResponse = {
  createProviderSession: {
    providerSession: ProviderSessionType;
  };
};

export const CREATE_PROVIDER_SESSION = gql`
  mutation CreateProviderSession($truentityIds: [ID!]!) {
    createProviderSession(truentityIds: $truentityIds) {
      providerSession {
        sessionId
        status
      }
    }
  }
`;

export type StartProviderSessionResponse = {
  startProviderSession: {
    authCode: string;
    status: string;
  };
};

export const START_PROVIDER_SESSION = gql`
  mutation StartProviderSession($sessionId: String!, $otpCode: String!) {
    startProviderSession(sessionId: $sessionId, otpCode: $otpCode) {
      authCode
      status
    }
  }
`;

export type CheckProviderSessionResponse = {
  checkProviderSession: {
    status: string;
    provider: ProviderType;
    dateRequested: string;
  };
};

export const CHECK_PROVIDER_SESSION = gql`
  mutation CheckProviderSession($sessionId: String!) {
    checkProviderSession(sessionId: $sessionId) {
      status
      provider {
        individualFirstName
        individualLastName
        npiNumber
        deaNumber
        stateLicense
        taxonomyCode
      }
      dateRequested
    }
  }
`;

export const GET_PROVIDER_SESSION_ACCOUNTS = gql`
  query GetProviderSessionAccountsQuery(
    $pageNum: Int!
    $pageSize: Int!
    $authCode: String!
    $relyingPartyId: String!
    $filterOptions: AccountFilterOptionsInput
  ) {
    getProviderSessionAccounts(
      pageNum: $pageNum
      pageSize: $pageSize
      authCode: $authCode
      relyingPartyId: $relyingPartyId
      filterOptions: $filterOptions
    ) {
      providerActivities {
        id
        createdAt
        requestedAt
        approvalRequestedAt
        rpmStatusUpdatedAt
        performedAt
        providerId
        account {
          id
          truentityId
          phone
          rpmApprovalStatus
          rpmStatus
          createdAt
          birthDate
          zipcode
          user {
            id
            verifiedEmail
            firstName
            lastName
          }
          currentMedsCount
          healthPlan {
            id
            orgName
          }
          lastMedrecDate
          lastImport {
            status
            lastImportDate
          }
          clientOrgs {
            name
            id
          }
          clientStores {
            name
            id
          }
          numQuickUploads
          numQuickUploadsNew
          numPendingTasks
          numCompletedTasks
          lastEncounterDate
          accountsAssignments {
            relyingPartyAdmin {
              id
              name
            }
          }
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetAccountMonitoringDevicesResponse = {
  getAccountDevices: {
    devices: AccountsMonitoringDeviceType[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
};

export type GetAccountMonitoringDeviceByIdResponse = {
  getAccountDeviceById: AccountsMonitoringDeviceType;
};

export type GetRpmClaimsResponse = {
  getRpmClaims: {
    billingAccounts: RpmClaimsType[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
};

export const GET_ACCOUNT_MONITORING_DEVICES = gql`
  query GetAccountDevices($pageSize: Int!, $pageNum: Int!, $truentityId: String!, $deviceStatus: String) {
    getAccountDevices(pageSize: $pageSize, pageNum: $pageNum, truentityId: $truentityId, deviceStatus: $deviceStatus) {
      devices {
        id
        externalPatientId
        hardwareId
        hardwarePatientId
        status
        vendorStatus
        connectedOn
        lastMeasurementAt
        gatewayId
        monitoringDeviceName
        gatewayConnectivityType
        formattedGatewayId
        signalStrength
        signalStrengthPercentage
        lastGatewayCheckingTime
        monitoringDevice {
          id
          brandName
          name
          typeName
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_ACCOUNT_MONITORING_DEVICE_NAMES = gql`
  query GetAccountDevices($pageSize: Int!, $pageNum: Int!, $truentityId: String!, $deviceStatus: String) {
    getAccountDevices(pageSize: $pageSize, pageNum: $pageNum, truentityId: $truentityId, deviceStatus: $deviceStatus) {
      devices {
        id
        monitoringDevice {
          id
          brandName
          name
        }
        latestReadings {
          id
          value
          status
          unit
          recordedAt
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const ADD_ACCOUNT_MONITORING_DEVICE = gql`
  mutation AddAccountDevice($truentityId: String!, $deviceType: String!, $deviceBrand: String!, $hardwareId: String!, $gatewayId: String) {
    addAccountDevice(
      truentityId: $truentityId
      deviceType: $deviceType
      deviceBrand: $deviceBrand
      hardwareId: $hardwareId
      gatewayId: $gatewayId
    ) {
      status
      message
    }
  }
`;

export const GET_PROVIDER_SESSION_ACCOUNT = gql`
  query GetProviderSessionAccount($authCode: String!, $providerActivityId: ID!) {
    getProviderSessionAccount(authCode: $authCode, providerActivityId: $providerActivityId) {
      account {
        truentityId
        zipcode
        address
        consent
        birthDate
        rpmApprovalStatus
        rpmStatus
        rpmStatusComment
        phone
        healthPlan {
          id
          orgName
        }
        gender
        data
        user {
          id
          verifiedEmail
          firstName
          lastName
        }
        clientOrgs {
          name
        }
        clientStores {
          name
        }
        accountsAssignments {
          relyingPartyAdmin {
            name
          }
        }
        lastImport {
          lastImportDate
          status
        }
        numPendingTasks
        numCompletedTasks
        lastEncounterDate
        doNotCall
        doNotCallLastSpecifiedAt
        accountsFollowupReminders {
          id
          followUpOn
          type
          mode
          appointmentRemindersEnabled
          subType
          notes
          status
          relyingPartyAdmin {
            id
            user {
              firstName
              lastName
              email
            }
          }
          createdAt
        }
        primaryLanguage
        currentMedsCount
        lastMedrecDate
        numQuickUploads
        numQuickUploadsNew
      }
      summary
      careSummaryUrls {
        url
        requestedOn
      }
      rpmEncounterUrl
    }
  }
`;

export const UPDATE_PROVIDER_SESSION_ACCOUNT_STATUS = gql`
  mutation UpdateProviderSessionAccountStatus($providerActivityId: ID!, $rpmStatus: String!, $authCode: String!, $comment: String) {
    updateProviderSessionAccountStatus(
      providerActivityId: $providerActivityId
      rpmStatus: $rpmStatus
      authCode: $authCode
      comment: $comment
    ) {
      account {
        id
        truentityId
        phone
        rpmStatus
        rpmStatusComment
        createdAt
        birthDate
        zipcode
      }
    }
  }
`;

export type EndProviderSessionResponse = {
  endProviderSession: {
    status: string;
  };
};

export const END_PROVIDER_SESSION = gql`
  mutation EndProviderSession($authCode: String!) {
    endProviderSession(authCode: $authCode) {
      status
    }
  }
`;

export const GET_CARE_PLAN_BY_ACCOUNT = gql`
  query GetCarePlanByAccountQuery($truentityId: ID!, $filterOptions: AccountCarePlansFilterOptionsInput) {
    getCarePlanByAccount(truentityId: $truentityId, filterOptions: $filterOptions) {
      consent {
        rpmProgramDescriptionAccepted
      }
      programDescription
      conditions
      outcomes
      goals
      barriers
    }
  }
`;

export const GET_RPM_CONSENT = gql`
  query GetRpmConsentQuery($truentityId: ID!) {
    getRpmConsent(truentityId: $truentityId) {
      id
      accountId
      rpmParticipation
      ccmParticipation
      rpmShareData
      jointCollaborationWithIndycare
      rpmWillReturnDevicesOnCancellation
      rpmCanCancelAnytime
      rpmObtainedOn
      rpmObtainedBy {
        user {
          id
          firstName
          lastName
        }
      }
      account {
        address
      }
    }
  }
`;

export const UPDATE_RPM_CONSENT = gql`
  mutation UpdateRpmConsent(
    $truentityId: ID!
    $rpmParticipation: Boolean!
    $ccmParticipation: Boolean!
    $rpmShareData: Boolean!
    $jointCollaborationWithIndycare: Boolean!
    $rpmCanCancelAnytime: Boolean!
    $rpmWillReturnDevicesOnCancellation: Boolean!
  ) {
    updateRpmConsent(
      truentityId: $truentityId
      rpmParticipation: $rpmParticipation
      ccmParticipation: $ccmParticipation
      rpmShareData: $rpmShareData
      jointCollaborationWithIndycare: $jointCollaborationWithIndycare
      rpmCanCancelAnytime: $rpmCanCancelAnytime
      rpmWillReturnDevicesOnCancellation: $rpmWillReturnDevicesOnCancellation
    ) {
      consent {
        id
        accountId
        rpmParticipation
        ccmParticipation
        rpmShareData
        jointCollaborationWithIndycare
        rpmCanCancelAnytime
        rpmWillReturnDevicesOnCancellation
        rpmObtainedOn
        account {
          truentityId
        }
        rpmObtainedBy {
          user {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export type AccountProviderType = {
  provider: ProviderType;
  isPrimaryCare?: boolean;
  isTruentityMonitoringProvider?: boolean;
};

export type RelyingPartyProviderType = {
  provider: ProviderType;
  isRpmReviewer: boolean;
  isPrimary: boolean;
};

export type SelectedProvidersType = {
  providerId: string;
} & ProviderMethodsTypes;

export type ProviderMethodsTypes = {
  isRpmReportProviderByFax: boolean;
  isRpmReportProviderByEmail: boolean;
  isRpmReportProviderByText: boolean;
};

export type GetAccountProvidersResponse = {
  accountProviders: {
    accountProviders: AccountProviderType[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
};

export type GetCommunicationLogsResponse = {
  getCommunicationLogs: {
    activityLogs: ActivityLogType[];
  };
};

export type GetAllProvidersResponse = {
  getAllProviders: {
    providers: ProviderType[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
};

export type GetRelyingPartyProvidersResponse = {
  relyingPartyProviders: {
    relyingPartyProviders: RelyingPartyProviderType[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
};
export type AccountProvidersResponse = {
  accountProviders: {
    accountProviders: AccountProviderType[];
    meta: Meta;
  };
};

export const GET_ACCOUNT_PROVIDERS = gql`
  query GetAccountProviders($truentityId: String!, $pageSize: Int, $pageNum: Int) {
    accountProviders(truentityId: $truentityId, pageSize: $pageSize, pageNum: $pageNum) {
      accountProviders {
        provider {
          id
          individualFirstName
          individualMiddleName
          individualLastName
          npiNumber
          deaNumber
          stateLicense
          taxonomyCode
          contacts {
            type
            value
          }
        }
        isPrimaryCare
        isTruentityMonitoringProvider
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export const GET_RELYING_PARTY_PROVIDERS = gql`
  query RelyingPartyProviders($relyingPartyId: ID!, $pageNum: Int!, $pageSize: Int!) {
    relyingPartyProviders(relyingPartyId: $relyingPartyId, pageNum: $pageNum, pageSize: $pageSize) {
      relyingPartyProviders {
        provider {
          id
          npiNumber
          individualLastName
          individualFirstName
          email
          phone
          contacts {
            type
            value
          }
        }
        rpmReportByText
        rpmReportByEmail
        rpmReportByFax
        isRpmReviewer
        isPrimary
      }
      meta {
        currentPage
        totalCount
      }
    }
  }
`;

export type GetCurrentRelyingPartyAdminProvidersResponse = {
  currentRelyingPartyAdminProviders: {
    relyingPartyAdmins: RelyingPartyAdminType[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
};

export const GET_CURRENT_RELYING_PARTY_ADMIN_PROVIDERS = gql`
  query CurrentRelyingPartyAdminProviders($pageNum: Int!, $pageSize: Int!, $requireSettingsPaths: [String!]) {
    currentRelyingPartyAdminProviders(pageNum: $pageNum, pageSize: $pageSize, requireSettingsPaths: $requireSettingsPaths) {
      relyingPartyAdmins {
        id
        npiNumber
        title
        settings
        user {
          firstName
          lastName
          email
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const CREATE_MULTIPLE_ACCOUNT_CONFIGURATIONS = gql`
  mutation CreateMultipleAccountConfigurations($truentityId: ID!, $configurationsInputs: AccountConfigurationsInput!) {
    createMultipleAccountConfigurations(truentityId: $truentityId, configurationsInputs: $configurationsInputs) {
      status
      message
    }
  }
`;

export const GET_ACCOUNT_CONFIGURATIONS = gql`
  query GetAccountConfigurations($truentityId: String!) {
    getAccountConfigurations(truentityId: $truentityId) {
      id
      rpmReportProviderByEmail
      rpmReportProviderByText
      rpmReportProviderByFax
      rpmReportPatientByEmail
      rpmReportPatientByText
      provider {
        id
      }
      account {
        id
        truentityId
      }
    }
  }
`;

export const GET_ACTIVITY_LOGS = gql`
  query ActivityLogsByFilter($pageNum: Int!, $pageSize: Int!, $activityTypes: [ActivityTypeEnum!]) {
    activityLogsByFilter(pageNum: $pageNum, pageSize: $pageSize, activityTypes: $activityTypes) {
      activityLogs {
        id
        logText
        account {
          truentityId
          user {
            firstName
            lastName
          }
        }
        activityType
        createdAt
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export const ASSIGN_MEDREC_TO_RPM_REPORT = gql`
  mutation AssignMedrecToRpmReport($truentityId: ID!, $medrecSnapshotId: String!) {
    assignMedrecToRpmReport(truentityId: $truentityId, medrecSnapshotId: $medrecSnapshotId) {
      status
      message
    }
  }
`;

export const GET_ACCOUNT_LAST_NOTE = gql`
  query GetAccountLastNote($truentityId: ID!) {
    getAccountLastNote(truentityId: $truentityId) {
      lastNoteDate
    }
  }
`;

export type AccountNotesFilterOptions = {
  monthYear?: string;
  shareWithProvider?: boolean;
  pinToProfile?: boolean;
};

export const GET_ACCOUNT_NOTES_BY_FILTER = gql`
  query GetAccountNotesByFilter($truentityId: ID!, $filterOptions: AccountNotesFilterOptionsInput!) {
    getAccountNotesByFilter(truentityId: $truentityId, filterOptions: $filterOptions) {
      id
      title
      note
      modeOfCapture
      shareWithProvider
      pinToProfile
      createdAt
    }
  }
`;

export const GET_DEVICE_BY_ID = gql`
  query GetAccountDeviceById($deviceId: String!) {
    getAccountDeviceById(deviceId: $deviceId) {
      id
      externalPatientId
      hardwareId
      hardwarePatientId
      status
      monitoringDevice {
        id
        brandName
        name
        typeName
      }
    }
  }
`;

export const UPDATE_ACCOUNT_NOTE = gql`
  mutation UpdateAccountNote(
    $truentityId: ID!
    $noteId: String!
    $modeOfCapture: String
    $description: String!
    $shareWithProvider: Boolean!
    $pinToProfile: Boolean!
  ) {
    updateAccountNote(
      truentityId: $truentityId
      noteId: $noteId
      modeOfCapture: $modeOfCapture
      description: $description
      shareWithProvider: $shareWithProvider
      pinToProfile: $pinToProfile
    ) {
      status
      message
    }
  }
`;

export const ARCHIVE_ACCOUNT_NOTES = gql`
  mutation ArchiveAccountNotes($truentityId: String!, $noteIds: [String!]!) {
    archiveAccountNotes(truentityId: $truentityId, noteIds: $noteIds) {
      status
      message
    }
  }
`;

export const UPDATE_ACCOUNT_NOTE_PROVIDERS = gql`
  mutation UpdateAccountNoteProviders($truentityId: ID!, $noteIds: [String!]!) {
    updateAccountNoteProviders(truentityId: $truentityId, noteIds: $noteIds) {
      status
      message
    }
  }
`;

export const UPDATE_ACCOUNT_NOTE_PIN = gql`
  mutation UpdateAccountNotePin($truentityId: ID!, $noteIds: [String!]!) {
    updateAccountNotePin(truentityId: $truentityId, noteIds: $noteIds) {
      status
      message
    }
  }
`;

export const DELETE_ACCOUNT_MONITORING_DEVICE = gql`
  mutation RemoveAccountDevice($truentityId: String!, $deviceId: String!) {
    removeAccountDevice(truentityId: $truentityId, deviceId: $deviceId) {
      status
      message
    }
  }
`;

export const UPDATE_ACCOUNT_MONITORING_DEVICE = gql`
  mutation UpdateAccountDevice(
    $truentityId: String!
    $deviceId: String!
    $deviceType: String!
    $deviceBrand: String!
    $hardwareId: String!
  ) {
    updateAccountDevice(
      truentityId: $truentityId
      deviceId: $deviceId
      deviceType: $deviceType
      deviceBrand: $deviceBrand
      hardwareId: $hardwareId
    ) {
      status
      message
    }
  }
`;

export const ADD_ACCOUNT_NOTE = gql`
  mutation CreateAccountNote($truentityId: ID!, $modeOfCapture: String!, $description: String!, $dateTime: String) {
    createAccountNote(truentityId: $truentityId, modeOfCapture: $modeOfCapture, description: $description, dateTime: $dateTime) {
      message
      status
    }
  }
`;

export type GetCoverLetterBodyResponse = {
  relyingParty: {
    coverLetterBody: string;
  };
};

export const GET_COVER_LETTER_BODY = gql`
  query GetRpmCoverLetterBody($relyingPartyId: ID!) {
    relyingParty(relyingPartyId: $relyingPartyId) {
      coverLetterBody
    }
  }
`;

export const GET_ACCOUNT_NOTE_BY_ID = gql`
  query GetAccountNotesById($truentityId: ID!, $noteId: String!) {
    getAccountNotesById(truentityId: $truentityId, noteId: $noteId) {
      title
      note
      modeOfCapture
      isArchived
      shareWithProvider
    }
  }
`;

export const GET_ACCOUNT_NOTES = gql`
  query GetAccountNotes($truentityId: ID!, $isArchived: Boolean, $pageNum: Int!, $pageSize: Int!) {
    getAccountNotes(truentityId: $truentityId, isArchived: $isArchived, pageNum: $pageNum, pageSize: $pageSize) {
      notes {
        id
        note
        title
        isArchived
        modeOfCapture
        shareWithProvider
        pinToProfile
        relyingPartyAdmin {
          name
        }
        createdAt
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export const GET_CARE_PLAN_VALUES = gql`
  query GetCarePlanValuesQuery {
    getCarePlanValues {
      conditions
      outcomes
      goals
      barriers
    }
  }
`;

export const GET_RPM_REVIEW_ACTIVITIES = gql`
  query GetRpmReviewActivitiesQuery($truentityId: ID!, $pageNum: Int!, $pageSize: Int!) {
    getRpmReviewActivities(truentityId: $truentityId, pageNum: $pageNum, pageSize: $pageSize) {
      careActivity {
        id
        title
        subTitle
        notes
        subType
        isEncounter
        totalTimeSpentSecs
        startedAt
        endedAt
        updatedAt
        performedBy {
          user {
            firstName
            lastName
          }
        }
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export const GET_RPM_ACTIVITIES = gql`
  query GetRpmActivitiesQuery($truentityId: ID!, $monthYear: String!, $subType: String!, $pageNum: Int!, $pageSize: Int!) {
    getRpmActivities(truentityId: $truentityId, monthYear: $monthYear, subType: $subType, pageNum: $pageNum, pageSize: $pageSize) {
      setupTotalTime
      monthlyTotalTime
      careActivity {
        id
        title
        subTitle
        notes
        subType
        type
        isEncounter
        startedAt
        endedAt
        totalTimeSpentSecs
        performedBy {
          user {
            firstName
            lastName
          }
        }
        contactMode
        readingType: vitalsType
        updatedAt
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export const ADD_RPM_ACTIVITY = gql`
  mutation AddRpmActivity($careActivityInput: RpmCareActivityInput!) {
    addRpmActivity(careActivityInput: $careActivityInput) {
      status
      message
    }
  }
`;

export const UPDATE_RPM_ACTIVITY = gql`
  mutation UpdateRpmActivity($careActivityInput: RpmCareActivityInput!, $activityId: ID!) {
    updateRpmActivity(careActivityInput: $careActivityInput, activityId: $activityId) {
      status
      message
    }
  }
`;

export const DELETE_RPM_ACTIVITY = gql`
  mutation DeleteRpmActivity($truentityId: ID!, $activityId: ID!) {
    deleteRpmActivity(truentityId: $truentityId, activityId: $activityId) {
      status
      message
    }
  }
`;

export const GET_COMMUNICATION_LOGS = gql`
  query GetCommunicationLogs($truentityId: String, $filterOptions: ActivityLogsFilterOptionsInput, $limit: Int) {
    getCommunicationLogs(truentityId: $truentityId, filterOptions: $filterOptions, limit: $limit) {
      activityLogs {
        id
        activityTime
        activityType
        logText
        data
      }
    }
  }
`;

export type AddProviderResponse = {
  createProviders: {
    status: string;
    message: string;
  };
};

export const ADD_PROVIDER = gql`
  mutation CreateProviders(
    $firstName: String!
    $lastName: String!
    $npiNumber: String!
    $title: String
    $email: String
    $phone: String
    $fax: String
  ) {
    createProviders(
      firstName: $firstName
      lastName: $lastName
      npiNumber: $npiNumber
      title: $title
      email: $email
      phone: $phone
      fax: $fax
    ) {
      status
      message
    }
  }
`;

export const GET_ALL_PROVIDERS = gql`
  query GetAllProviders($pageSize: Int!, $pageNum: Int!) {
    getAllProviders(pageSize: $pageSize, pageNum: $pageNum) {
      providers {
        id
        individualFirstName
        individualMiddleName
        individualLastName
        email
        phone
        fax
        npiNumber
        title
        deaNumber
        stateLicense
        hasValidSession
        taxonomyCode
        contacts {
          type
          value
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_PROVIDERS_BY_FILTER = gql`
  query GetAllProviders($pageSize: Int!, $pageNum: Int!, $filterOptions: ProviderFilterOptionsInput!, $truentityId: String) {
    providersLookup(pageSize: $pageSize, pageNum: $pageNum, filterOptions: $filterOptions, truentityId: $truentityId) {
      providers {
        id
        individualFirstName
        individualMiddleName
        individualLastName
        email
        phone
        fax
        npiNumber
        title
        deaNumber
        stateLicense
        taxonomyCode
        specialty
        lastEncounterDate
        lastRxDate
        contacts {
          type
          value
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetProvidersByFilterResponse = {
  providersLookup: {
    providers: ProviderType[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
};

export type UpdateProviderResponse = {
  updateProvider: {
    status: string;
    errors: string[];
  };
  errors: string[];
};

export const UPDATE_PROVIDER = gql`
  mutation UpdateProvider($id: ID!, $input: UpdateProviderInput!) {
    updateProvider(id: $id, input: $input) {
      status
      errors
    }
  }
`;

export const UPDATE_ACCOUNT_PROVIDERS = gql`
  mutation UpdateAccountProviders($truentityId: ID!, $providerIds: [String!]!) {
    updateAccountProviders(truentityId: $truentityId, providerIds: $providerIds) {
      status
      message
    }
  }
`;

export const UPDATE_RELYING_PARTY_PROVIDERS = gql`
  mutation UpdateRelyingPartyProviders($providerIds: [String!]!, $relyingPartyId: ID) {
    updateRelyingPartyProviders(providerIds: $providerIds, relyingPartyId: $relyingPartyId) {
      status
      message
    }
  }
`;

export const GET_ACCOUNT_VITALS_DATA_AVAILABILITY = gql`
  query GetAccountVitalsDataAvailabilityQuery($truentityId: String!) {
    getAccountVitalsDataAvailability(truentityId: $truentityId) {
      isBloodGlucoseDataAvailable
      isBloodPressureDataAvailable
      isHeartRateDataAvailable
    }
  }
`;

export const UPDATE_ACCOUNTS_PROVIDER_PRIMARY_CARE = gql`
  mutation UpdateProviderPrimaryCare($truentityId: ID!, $providerId: String!) {
    updateProviderPrimaryCare(truentityId: $truentityId, providerId: $providerId) {
      status
      message
    }
  }
`;

export type GetRpmReportsWithProviderSession = {
  getProviderSessionRpmReports: {
    providerActivities: RpmProviderActivitiesType[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
};
export type GetProviderSessionRpmReportResponse = {
  getProviderSessionRpmReport: RpmProviderActivitiesType;
};

export const GET_PROVIDER_SESSION_RPM_REPORTS = gql`
  query GetProviderSessionRpmReportsQuery(
    $authCode: String!
    $relyingPartyId: String!
    $filterOptions: RpmReportsFilterOptionsInput!
    $pageSize: Int!
    $pageNum: Int!
  ) {
    getProviderSessionRpmReports(
      authCode: $authCode
      filterOptions: $filterOptions
      pageSize: $pageSize
      pageNum: $pageNum
      relyingPartyId: $relyingPartyId
    ) {
      providerActivities {
        id
        status
        performedAt
        createdAt
        requestedAt
        rpmReport {
          id
          status
          monthYear
          reportStartAt
          reportEndAt
          reportedDocS3Key
          lastReportedOn
          account {
            id
            truentityId
            birthDate
            user {
              id
              verifiedEmail
              firstName
              lastName
            }
          }
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;
export const GET_PROVIDER_SESSION_RPM_REPORT = gql`
  query GetProviderSessionRpmReportQuery($authCode: String!, $providerActivityId: ID!) {
    getProviderSessionRpmReport(authCode: $authCode, providerActivityId: $providerActivityId) {
      id
      status
      performedAt
      createdAt
      rpmReport {
        id
        status
        monthYear
        reportStartAt
        reportEndAt
        reportedDocS3Key
        lastReportedOn
        account {
          id
          truentityId
          gender
          birthDate
          phone
          healthPlan {
            id
            orgName
          }
          user {
            id
            verifiedEmail
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export type UpdateProviderSessionRpmReportStatusResponse = {
  updateProviderSessionRpmReportStatus: {
    status: string;
    message: string;
  };
};

export const UPDATE_PROVIDER_SESSION_RPM_REPORT_STATUS = gql`
  mutation UpdateProviderSessionRpmReportStatus(
    $providerActivityId: String!
    $reviewStatus: String!
    $authCode: String!
    $comment: String
  ) {
    updateProviderSessionRpmReportStatus(
      providerActivityId: $providerActivityId
      reviewStatus: $reviewStatus
      authCode: $authCode
      comment: $comment
    ) {
      status
      message
    }
  }
`;

export type GetAccountDiagnosisResponseType = {
  getAccountDiagnoses: {
    diagnoses: Diagnosis[];
    meta?: {
      totalPages: number;
      totalCount: number;
    };
  };
};

export const GET_ACCOUNT_DIAGNOSES = gql`
  query GetAccountDiagnosesQuery($truentityId: String!, $filterOptions: DiagnosesFilterOptionsInput, $pageSize: Int, $pageNum: Int) {
    getAccountDiagnoses(truentityId: $truentityId, filterOptions: $filterOptions, pageSize: $pageSize, pageNum: $pageNum) {
      diagnoses {
        id
        code
        name
        category
        system
        isChronic
        subscribedToMedicalSubService
      }
    }
  }
`;

type EnrolmentStats = {
  enrolledAccountsCount: number;
  unenrolledAccountsCount: number;
  candidateAccountsCount: number;
  scheduledForEnrollmentAccountsCount: number;
  scheduledForEnrollmentAccountsPercentage: number;
  enrolledAccountsPercentage: number;
  candidateAccountsPercentage: number;
  totalEnrolledAccountsCount: number;
  totalCandidateAccountsCount: number;
};

type ReadingsStats = {
  activeAccountsCount: number;
  inactiveAccountsCount: number;
  todayAccountsReadingsCount: number;
  monthlyReadingsBrackets: {
    name: string;
    count: number;
    percentage: number;
  }[];
};

type ActivitiesStats = {
  totalDevicesCount: number;
  totalUnreadAlertsCount: number;
  accountsDeviceTypeBrackets: {
    name: string;
    count: number;
    percentage: number;
  }[];
  accountsTimeSpentBrackets: {
    name: string;
    count: number;
    percentage: number;
  }[];
};

export type RpmStatisticsFilterOptionsInput = {
  organization: string;
  startDate?: string;
  endDate?: string;
};

export type RpmStatistics = {
  enrollmentStats: EnrolmentStats;
  readingsStats: ReadingsStats;
  activitiesStats: ActivitiesStats;
};
export type GetRpmStatisticsResponse = {
  getRpmStatistics: RpmStatistics;
};

export type GetRpmAccountsCountByStatusResponse = {
  getRpmAccountsCountByStatus: {
    count: number;
  };
};

export const GET_RPM_STATISTICS = gql`
  query GetRpmStatisticsQuery($filterOptions: RpmStatisticsFilterOptionsInput!) {
    getRpmStatistics(filterOptions: $filterOptions) {
      enrollmentStats {
        enrolledAccountsCount
        candidateAccountsCount
        scheduledForEnrollmentAccountsCount
        scheduledForEnrollmentAccountsPercentage
        enrolledAccountsPercentage
        candidateAccountsPercentage
        totalEnrolledAccountsCount
        totalCandidateAccountsCount
      }
      readingsStats {
        activeAccountsCount
        inactiveAccountsCount
        todayAccountsReadingsCount
        monthlyReadingsBrackets {
          name
          count
          percentage
        }
      }
      activitiesStats {
        totalDevicesCount
        totalUnreadAlertsCount
        accountsTimeSpentBrackets {
          name
          count
          percentage
        }
        accountsDeviceTypeBrackets {
          name
          count
          percentage
        }
      }
    }
  }
`;

export const REFRESH_RPM_STATISTICS = gql`
  mutation RefreshRpmStatisticsSnapshot($filterOptions: RpmStatisticsFilterOptionsInput!) {
    refreshRpmStatisticsSnapshot(filterOptions: $filterOptions) {
      status
      message
    }
  }
`;

export const GET_RPM_ACCOUNTS_COUNT_BY_STATUS = gql`
  query GetRpmAccountsCountByStatusQuery($status: String!) {
    getRpmAccountsCountByStatus(status: $status) {
      count
    }
  }
`;

export type RpmSetupTypes = {
  id: string;
  type: string;
  status: string;
  isRequiredForEnrollment: boolean;
  isManualStatusUpdate: boolean;
  createdAt: string;
  canCurrentTabBeCompleted: boolean;
  isDisabled: boolean;
  isReadOnly: boolean;
  shouldRefreshTab?: boolean;
};

export type GetRpmSetupResponse = {
  getRpmSetup: RpmSetupTypes[];
};

export const GET_RPM_SETUPS = gql`
  query GetRpmSetupQuery($truentityId: String!) {
    getRpmSetup(truentityId: $truentityId) {
      id
      type
      status
      isRequiredForEnrollment
      isManualStatusUpdate
      createdAt
    }
  }
`;

export type CreateRpmSetupResponse = {
  createRpmSetup: {
    status: string;
    message: string[];
    rpmSetups: RpmSetupTypes[];
  };
};

export const CREATE_RPM_SETUPS = gql`
  mutation CreateRpmSetup($truentityId: ID!, $rpmSetupValues: [RpmSetupValuesInput!]!) {
    createRpmSetup(truentityId: $truentityId, rpmSetupValues: $rpmSetupValues) {
      status
      message
      rpmSetups {
        id
        type
        status
        isRequiredForEnrollment
        isManualStatusUpdate
        createdAt
      }
    }
  }
`;

export type UpdateRpmSetupResponse = {
  updateRpmSetup: {
    status: string;
    message: string;
    rpmSetup: RpmSetupTypes;
  };
};

export const UPDATE_RPM_SETUP = gql`
  mutation UpdateRpmSetup($rpmSetupId: String!, $status: String, $isRequiredForEnrollment: Boolean, $isManualStatusUpdate: Boolean) {
    updateRpmSetup(
      rpmSetupId: $rpmSetupId
      status: $status
      isRequiredForEnrollment: $isRequiredForEnrollment
      isManualStatusUpdate: $isManualStatusUpdate
    ) {
      status
      message
      rpmSetup {
        id
        type
        status
        isRequiredForEnrollment
        isManualStatusUpdate
        createdAt
      }
    }
  }
`;

export type GetAccountAlertLimitsResponse = {
  getAccountAlertLimitsConfigurations: AlertLimitsType;
};

export const GET_ACCOUNT_ALERT_LIMITS = gql`
  query GetAccountAlertLimitsConfigurations($truentityId: String!) {
    getAccountAlertLimitsConfigurations(truentityId: $truentityId) {
      bloodGlucose {
        cLowValue
        lowValue
        highValue
        cHighValue
      }
      heartRate {
        cLowValue
        lowValue
        highValue
        cHighValue
      }
      sysBloodPressure {
        cLowValue
        lowValue
        highValue
        cHighValue
      }
      diaBloodPressure {
        cLowValue
        lowValue
        highValue
        cHighValue
      }
    }
  }
`;

export type UpdateAccountAlertLimitsResponse = {
  updateAccountAlertLimits: {
    accountClinicalLevels: {
      bloodGlucose: AlertLimitType;
      heartRate: AlertLimitType;
      sysBloodPressure: AlertLimitType;
      diaBloodPressure: AlertLimitType;
    };
    status: string;
    errors: string[];
  };
};

export const UPDATE_ACCOUNT_ALERT_LIMITS = gql`
  mutation UpdateAccountAlertLimits($truentityId: ID!, $alertLimitsInput: AccountAlertLimitsInput!) {
    updateAccountAlertLimits(truentityId: $truentityId, alertLimitsInput: $alertLimitsInput) {
      accountClinicalLevels {
        bloodGlucose {
          cLowValue
          lowValue
          highValue
          cHighValue
        }
        heartRate {
          cLowValue
          lowValue
          highValue
          cHighValue
        }
      }
      status
      errors
    }
  }
`;

export type SendOtpMailToProviderResponse = {
  sendOtpMailToProvider: {
    status: string;
    message: string;
  };
};

export const SEND_OTP_MAIL_TO_PROVIDER = gql`
  mutation SendOtpMailToProvider($providerId: String!, $providerOtpOptions: OtpOptionsInput!) {
    sendOtpMailToProvider(providerId: $providerId, providerOtpOptions: $providerOtpOptions) {
      status
      message
    }
  }
`;

export const GET_CLAIMS = gql`
  query GetRpmClaimsQuery($monthYear: String!, $pageNum: Int!, $pageSize: Int!, $isExport: Boolean!, $all: Boolean) {
    getRpmClaims(monthYear: $monthYear, pageNum: $pageNum, pageSize: $pageSize, isExport: $isExport, all: $all) {
      billingAccounts {
        id
        relyingParty {
          id
          name
        }
        account {
          accountsMonitoringDevices {
            externalPatientId
            status
          }
          rpmEnrolledAt
          rpmStatus
          birthDate
          truentityId
          user {
            firstName
            lastName
          }
          billingHoldActive
          activeBillingHoldReasons
        }
        onHold
        holdReasons {
          reason
          label
          detail
          severity
          startedAt
          billingMonth
        }
        ehrId
        description
        diagnosesCodes
        diagnosesNames
        cptCode
        recordedAt
        dateOfService
        provider {
          name
          npiNumber
        }
        billingType
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export type DeleteVitalsReadingResponse = {
  deleteAccountVitalsReading: {
    status: string;
  };
};

export const DELETE_ACCOUNT_VITALS_READING = gql`
  mutation DeleteAccountVitalsReading($id: ID!) {
    deleteAccountVitalsReading(id: $id) {
      status
    }
  }
`;

export const GET_RELYING_PARTY_ALERT_LIMITS = gql`
  query GetRelyingPartyAlertLimitsConfigurations($relyingPartyId: String!) {
    getRelyingPartyAlertLimitsConfigurations(relyingPartyId: $relyingPartyId) {
      bloodGlucose {
        cLowValue
        lowValue
        highValue
        cHighValue
      }
      heartRate {
        cLowValue
        lowValue
        highValue
        cHighValue
      }
      sysBloodPressure {
        cLowValue
        lowValue
        highValue
        cHighValue
      }
      diaBloodPressure {
        cLowValue
        lowValue
        highValue
        cHighValue
      }
    }
  }
`;

export type GetRelyingPartyAlertLimitsResponse = {
  getRelyingPartyAlertLimitsConfigurations: AlertLimitsType;
};

export const UPDATE_RELYING_PARTY_ALERT_LIMITS = gql`
  mutation UpdateRelyingPartyAlertLimits($relyingPartyId: ID!, $submitType: SubmitTypeEnum!, $alertLimitsInput: AccountAlertLimitsInput) {
    updateRelyingPartyAlertLimits(relyingPartyId: $relyingPartyId, submitType: $submitType, alertLimitsInput: $alertLimitsInput) {
      relyingPartyClinicalLevels {
        bloodGlucose {
          cLowValue
          lowValue
          highValue
          cHighValue
        }
        heartRate {
          cLowValue
          lowValue
          highValue
          cHighValue
        }
      }
      status
      message
    }
  }
`;

export type UpdateRelyingPartyAlertLimitsResponse = {
  updateRelyingPartyAlertLimits: {
    relyingPartyClinicalLevels: {
      bloodGlucose: AlertLimitType;
      heartRate: AlertLimitType;
      sysBloodPressure: AlertLimitType;
      diaBloodPressure: AlertLimitType;
    };
    status: string;
    message: string;
  };
};

export type GetProviderRelyingPartiesResponse = {
  getProviderSessionRelyingParties: {
    relyingParties: {
      id: string;
      name: string;
    }[];
  };
};

export const GET_PROVIDER_SESSION_RELYING_PARTIES = gql`
  query GetProviderSessionRelyingParties($authCode: String!) {
    getProviderSessionRelyingParties(authCode: $authCode) {
      relyingParties {
        id
        name
      }
    }
  }
`;

export type RpmDeviceDetails = {
  id: string;
  name: string;
  status: string;
  deviceBrand: string;
  deviceType: string;
  isDeviceUsedByAccountOwner: boolean;
  isDeviceUsedByOtherUsers: boolean;
};

export type GetDeviceDetailsByGatewayIdTypes = {
  getDeviceDetailsByGatewayId: RpmDeviceDetails[];
};

export const GET_DEVICE_DETAILS_BY_GATEWAY_ID = gql`
  query GetDeviceDetailsByGatewayId($gatewayId: String!, $truentityId: ID!, $filterDeviceTypes: [String!]) {
    getDeviceDetailsByGatewayId(gatewayId: $gatewayId, truentityId: $truentityId, filterDeviceTypes: $filterDeviceTypes) {
      id
      name
      status
      deviceBrand
      deviceType
      isDeviceUsedByAccountOwner
      isDeviceUsedByOtherUsers
    }
  }
`;

export type GetRpmReportByAccountResponse = {
  getRpmReportByAccount: Partial<RpmReportsType>;
};

export const GET_RPM_REPORT_BY_ACCOUNT = gql`
  query GetRpmReportByAccount($truentityId: ID!, $monthYear: String) {
    getRpmReportByAccount(truentityId: $truentityId, monthYear: $monthYear) {
      id
      monthYear
      reportedDocS3Key
      account {
        user {
          firstName
          lastName
        }
      }
    }
  }
`;

export type GetRpmSummeryReportByAccountResponse = {
  getRpmSummeryReportByAccount: Partial<RpmReportsType>;
};

export const GET_RPM_SUMMERY_REPORT_BY_ACCOUNT = gql`
  query GetRpmSummeryReportByAccountQuery($truentityId: ID!, $monthYear: String) {
    getRpmSummeryReportByAccount(truentityId: $truentityId, monthYear: $monthYear) {
      id
      monthYear
      summaryReportS3Key
      account {
        user {
          firstName
          lastName
        }
      }
    }
  }
`;

export type ReviewAccountsRpmReportsResponse = {
  reviewAccountsRpmReports: {
    status: string;
    message: string;
  };
};

export const REVIEW_ACCOUNTS_RPM_REPORTS = gql`
  mutation ReviewAccountsRpmReports($truentityIds: [String!]!, $reviewSubmitType: ReviewSubmitTypeEnum!, $monthYear: String!) {
    reviewAccountsRpmReports(truentityIds: $truentityIds, reviewSubmitType: $reviewSubmitType, monthYear: $monthYear) {
      status
      message
    }
  }
`;

export type GetRpmReportsCommunicationsResponse = {
  getRpmReportCommunicationActivities: {
    rpmReportsCommunications: RpmReportsCommunicationType[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
};

export const GET_RPM_REPORTS_COMMUNICATIONS = gql`
  query GetRpmReportCommunicationActivities($rpmReportStatusType: RpmReportDailySendStatusTypeEnum!, $pageNum: Int!, $pageSize: Int!) {
    getRpmReportCommunicationActivities(rpmReportStatusType: $rpmReportStatusType, pageNum: $pageNum, pageSize: $pageSize) {
      rpmReportsCommunications {
        rpmReport {
          id
          createdAt
          updatedAt
          account {
            truentityId
            rpmEnrolledAt
            user {
              firstName
              lastName
              middleName
            }
          }
          lastReportedOn
          reportedDocS3Key
          summaryReportS3Key
          statusUpdatedAt
          monthYear
          dailySendStatus
          reviewProviders {
            provider {
              id
              individualFirstName
              individualLastName
            }
            reportSendingContacts {
              id
              type
              value
              status
              message
            }
          }
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type UpdateRpmReportsStatusesResponse = {
  updateRpmReportsStatuses: {
    status: string;
    message: string;
  };
};

export type UpdateRpmReportsStatusesVariables = {
  rpmReportIds: string[];
  rpmDailySendStatus: UpdateRpmReportStatusTypeEnum;
};

export const UPDATE_RPM_REPORTS_STATUSES = gql`
  mutation UpdateRpmReportsStatuses($rpmReportIds: [ID!]!, $rpmDailySendStatus: RpmReportDailySendStatusTypeEnum!) {
    updateRpmReportsStatuses(rpmReportIds: $rpmReportIds, rpmDailySendStatus: $rpmDailySendStatus) {
      status
      message
    }
  }
`;
export type AccountsGatewayHardwareType = {
  deviceId: string;
  hardwareId: string;
  gatewayId: string;
  formattedGatewayId: string;
  status: string;
  lastGatewayCheckingTime: string;
  signalStrength: string;
  deviceBrandName: string;
  deviceTypeName: string;
  deviceName: string;
  truentityId: string;
  birthDate: string;
  zipcode: string;
  rpmStatus: string;
  user: UserType;
  phone: string;
  latestReadings: {
    id: string;
    value: string;
    unit: string;
    recordedAt: string;
  }[];
};

export type GetAccountsByGatewayAndDeviceIdResponse = {
  getAccountsByGatewayDeviceId: {
    rpmAccounts: AccountsGatewayHardwareType[];
    meta: Meta;
  };
};

export const GET_ACCOUNTS_BY_GATEWAY_AND_DEVICE_ID = gql`
  query GetAccountsByGatewayDeviceId($pageNum: Int!, $pageSize: Int!, $id: String!) {
    getAccountsByGatewayDeviceId(pageNum: $pageNum, pageSize: $pageSize, id: $id) {
      rpmAccounts {
        deviceId
        hardwareId
        gatewayId
        formattedGatewayId
        signalStrength
        lastGatewayCheckingTime
        status
        truentityId
        birthDate
        zipcode
        rpmStatus
        phone
        deviceBrandName
        deviceTypeName
        deviceName
        user {
          firstName
          lastName
        }
        latestReadings {
          id
          value
          unit
          recordedAt
        }
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export type GetRpmReportsCountsResponse = {
  getRpmReportsCounts: {
    readyForSending: number;
    scheduledForSending: number;
    sent: number;
    failed: number;
  };
};

export const GET_RPM_REPORTS_COUNTS = gql`
  query GetRpmReportsCountsQuery {
    getRpmReportsCounts {
      readyForSending
      scheduledForSending
      sent
      failed
    }
  }
`;

export type GetRpmFollowupConsultsType = {
  getRpmFollowupConsults: {
    rpmFollowupConsults: RpmFollowupConsultType[];
  };
};

export const GET_RPM_FOLLOWUP_CONSULTS = gql`
  query GetRpmFollowupConsultsQuery($filterOptions: RpmFollowupConsultsFilterOptionsInput!) {
    getRpmFollowupConsults(filterOptions: $filterOptions) {
      rpmFollowupConsults {
        id
        consultAt
      }
    }
  }
`;

export type GetRpmFollowupConsultType = {
  getRpmFollowupConsult: RpmFollowupConsultType;
};

export const GET_RPM_FOLLOWUP_CONSULT = gql`
  query GetRpmFollowupConsultQuery($followupConsultId: String!) {
    getRpmFollowupConsult(followupConsultId: $followupConsultId) {
      id
      consultAt
      signOffAt
      reviewProvider {
        name
      }
      reportS3Key
      accountsFollowupReminder {
        id
        followUpOn
        type
        mode
        appointmentRemindersEnabled
        relyingPartyAdmin {
          id
          name
          roleType
        }
      }
      status
      statusUpdatedAt
      isTelehealthVisitLaunched
      amendable
      amendmentStatus
      lastAmendedAt
      amendmentCount
    }
  }
`;

export type GetAccountSmartGoalResponse = {
  getAccountSmartGoals: {
    accountGoals: AccountSmartGoalType[];
    meta: Meta;
  };
};

export const GET_ACCOUNT_SMART_GOALS = gql`
  query GetAccountSmartGoals($truentityId: ID!, $filterOptions: SmartGoalsFilterOptionsInput, $pageNum: Int, $pageSize: Int) {
    getAccountSmartGoals(truentityId: $truentityId, filterOptions: $filterOptions, pageNum: $pageNum, pageSize: $pageSize) {
      accountGoals {
        id
        goalContent
        status
        statusChangeAt
        isArchived
        rpmFollowupConsult {
          id
        }
        createdAt
        updatedAt
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export const UPDATE_ACCOUNT_SMART_GOAL = gql`
  mutation UpdateAccountSmartGoal($accountSmartGoalId: ID!, $status: AccountSmartGoalStatusEnum, $isArchived: Boolean) {
    updateAccountSmartGoal(accountSmartGoalId: $accountSmartGoalId, status: $status, isArchived: $isArchived) {
      status
      message
    }
  }
`;

export type GetGoalTemplatesResponse = {
  getAllTemplates: SmartGoalCategory[];
};

export const GET_GOAL_TEMPLATES = gql`
  query GetAllTemplatesQuery($categories: [AccountDiagnosisCategoryEnum!], $goalId: String) {
    getAllTemplates(categories: $categories, goalId: $goalId) {
      category
      subCategory {
        categoryName
        templates {
          id
          templateName
          templateText
          category
          subCategory
          placeholders
          validationRules
        }
      }
    }
  }
`;

export const CREATE_ACCOUNT_SMART_GOAL = gql`
  mutation CreateAccountSmartGoal(
    $truentityId: ID!
    $goals: [AccountSmartGoalInput!]!
    $customGoals: [String!]
    $rpmFollowupConsultId: String
  ) {
    createAccountSmartGoal(
      truentityId: $truentityId
      goals: $goals
      customGoals: $customGoals
      rpmFollowupConsultId: $rpmFollowupConsultId
    ) {
      status
      message
    }
  }
`;

export const UPDATE_RPM_ACCOUNT_STATUS = gql`
  mutation UpdateRpmAccountStatus($truentityId: String!, $updateRpmAccountStatusInput: UpdateRpmAccountStatusInput!) {
    updateRpmAccountStatus(truentityId: $truentityId, updateRpmAccountStatusInput: $updateRpmAccountStatusInput) {
      account {
        id
        truentityId
        phone
        rpmStatus
        rpmStatusComment
        createdAt
        birthDate
        zipcode
      }
      ccmEnrolled
      ccmEnrollmentMessage
    }
  }
`;

export const UPDATE_ORDER_CHECKED = gql`
  mutation UpdateOrderChecked($truentityId: ID!, $orderChecked: Boolean!) {
    updateOrderChecked(truentityId: $truentityId, orderChecked: $orderChecked) {
      accountSummary {
        id
        orderChecked
        orderDatetime
        orderPdf
      }
    }
  }
`;

export type UpdateRpmAccountStatusResponse = {
  updateRpmAccountStatus: {
    account: PatientsData;
  } & CcmAutoEnrollmentFields;
};
export const UPDATE_RPM_ACCOUNT_MONITORED_CONDITION = gql`
  mutation UpdateRpmAccountMonitoredCondition(
    $truentityId: ID!
    $conditions: [AccountDiagnosisCategoryEnum!]!
    $medicalServiceName: String
  ) {
    updateRpmAccountMonitoredCondition(truentityId: $truentityId, conditions: $conditions, medicalServiceName: $medicalServiceName) {
      status
      message
    }
  }
`;

export type ChangeAccountTelehealthVisitLaunchedFlagResponse = {
  changeAccountTelehealthVisitLaunchedFlag: {
    status: string;
    message: string;
    account: AccountType;
  };
};

export const CHANGE_ACCOUNT_TELEHEALTH_VISIT_LAUNCHED_FLAG = gql`
  mutation ChangeAccountTelehealthVisitLaunchedFlag($truentityId: ID!, $isAccountTelehealthVisitLaunched: Boolean!) {
    changeAccountTelehealthVisitLaunchedFlag(
      truentityId: $truentityId
      isAccountTelehealthVisitLaunched: $isAccountTelehealthVisitLaunched
    ) {
      status
      message
      account {
        accountSummary {
          isTelehealthVisitLaunched
        }
      }
    }
  }
`;

export const GET_ACCOUNT_ORDERS = gql`
  query GetAccountOrders($truentityId: ID!, $medicalServices: [MedicalServiceTypeEnum!]) {
    getAccountOrders(truentityId: $truentityId, medicalServices: $medicalServices) {
      orderChecked
      orderDatetime
      orderPdf
      orderType
    }
  }
`;

export type GetAccountOrdersResponse = {
  getAccountOrders: AccountOrderType[];
};

export const UPDATE_ACCOUNT_ORDER = gql`
  mutation UpdateAccountOrder($truentityId: ID!, $orderChecked: Boolean!, $orderType: String!) {
    updateAccountOrder(truentityId: $truentityId, orderChecked: $orderChecked, orderType: $orderType) {
      accountOrder {
        orderChecked
        orderDatetime
        orderPdf
        orderType
      }
    }
  }
`;

export type UpdateAccountOrderResponse = {
  updateAccountOrder: {
    accountOrder: AccountOrderType;
  };
};

export const GET_RPM_KPI_STATISTICS_QUERY = gql`
  query GetRpmKpiStatisticsQuery($includeCcmEnrolled: Boolean) {
    getRpmKpiStatistics(includeCcmEnrolled: $includeCcmEnrolled) {
      criticallyHigh
      criticallyLow
      high
      low
      medRegimenCount
      noReadings
      rpmSetup
      criticalPatients
      abnormalPatients
      medRegimenPatients
      noReadingsPatients
      rpmSetupPatients
      enrolledThisMonth
      enrolledYesterday
      enrolledTotal
      ccmEnrolledThisMonth
      ccmEnrolledYesterday
      uniqueEnrolledThisMonth
      uniqueEnrolledYesterday
      bpmCount
      bgmCount
      readingsCategories {
        name
        value
        accounts {
          truentityId
          user {
            firstName
            lastName
          }
        }
      }
      treatmentsCategories {
        name
        value
        accounts {
          truentityId
          user {
            firstName
            lastName
          }
        }
      }
      rpmTreatmentsCategories {
        name
        value
        accounts {
          truentityId
          user {
            firstName
            lastName
          }
        }
      }
      ccmTreatmentsCategories {
        name
        value
        accounts {
          truentityId
          user {
            firstName
            lastName
          }
        }
      }
      treatmentsCategoriesOnPace {
        name
        value
        percentage
        accounts {
          truentityId
          user {
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export interface ReadingCategory {
  name: string;
  value: number;
  percentage: number;
  accounts?: AccountType[];
}

export type GetRpmKpiStatisticsQueryResponse = {
  getRpmKpiStatistics: {
    criticallyHigh: number;
    criticallyLow: number;
    high: number;
    low: number;
    medRegimenCount: number;
    bpmCount: number;
    bgmCount: number;
    noReadings: number;
    rpmSetup: number;
    criticalPatients: number;
    abnormalPatients: number;
    medRegimenPatients: number;
    noReadingsPatients: number;
    rpmSetupPatients: number;
    enrolledThisMonth: number;
    enrolledYesterday: number;
    enrolledTotal: number;
    ccmEnrolledThisMonth: number;
    ccmEnrolledYesterday: number;
    uniqueEnrolledThisMonth: number;
    uniqueEnrolledYesterday: number;
    highReadings: number;
    lowReadings: number;
    medReadings: number;
    readingsCategories: Required<ReadingCategory>[];
    treatmentsCategories: Required<ReadingCategory>[];
    rpmTreatmentsCategories: Required<ReadingCategory>[];
    ccmTreatmentsCategories: Required<ReadingCategory>[];
    treatmentsCategoriesOnPace: Required<ReadingCategory>[];
  };
};

export type ActiveRelyingPartyAdminSubscriptionResponse = {
  relyingPartyAdminEngagementTracking: {
    adminsWorkingOnPatientActivity: ActivityLogType[];
  };
};

export const ON_RELYING_PARTY_ADMIN_ACTIVITY_ENGAGEMENT_TRACKER = gql`
  subscription OnRelyingPartyAdminActivityEngagementTracker($truentityId: String!) {
    relyingPartyAdminEngagementTracking(truentityId: $truentityId) {
      adminsWorkingOnPatientActivity {
        id
        logText
        activityType
        relyingPartyAdmin {
          id
          roleType
          user {
            firstName
            lastName
            provider {
              title
            }
          }
        }
      }
    }
  }
`;

export type PatientSetupDataUpdateNotificationResponse = {
  account: AccountType;
  updatedBy: RelyingPartyAdminType;
  affectedTab: string;
  action: string;
  lastUpdatedTime: string;
};

export const ON_PATIENT_SETUP_DATA_CHANGE = gql`
  subscription OnPatientSetupDataChange($truentityId: ID!, $rpmSetupType: String!) {
    patientSetupDataUpdateNotification(truentityId: $truentityId, rpmSetupType: $rpmSetupType) {
      affectedTab
      action
      lastUpdatedTime
    }
  }
`;

export const GET_RPM_MONTHLY_REPORTS_FOR_REVIEW = gql`
  query GetRpmReportsMonthlyQuery($truentityId: ID!, $includeCurrentMonth: Boolean) {
    getRpmReportsMonthly(truentityId: $truentityId, includeCurrentMonth: $includeCurrentMonth) {
      monthYearName
      report {
        id
        status
        reportedDocS3Key
        rpmAccountReviewWorkflow {
          id
          signOffAt
        }
      }
    }
  }
`;

export type GetRpmAccountReviewWorkflowResponse = {
  getRpmAccountReviewWorkflow: PatientReviewWorkflowType;
};

export const GET_PATIENT_RPM_REVIEW_WORKFLOW = gql`
  query GetRpmAccountReviewWorkflow($accountReviewWorkflowId: ID!) {
    getRpmAccountReviewWorkflow(accountReviewWorkflowId: $accountReviewWorkflowId) {
      signOffAt
      completedAt
    }
  }
`;

export const START_PATIENT_REVIEW_WORKFLOW = gql`
  mutation CreateAccountReviewWorkflow($rpmReportId: ID!) {
    createAccountReviewWorkflow(rpmReportId: $rpmReportId) {
      status
      message
    }
  }
`;

export const SAVE_PATIENT_REVIEW_NOTE = gql`
  mutation CreateAccountReviewNote(
    $accountReviewWorkflowId: ID!
    $note: String!
    $accountReviewNoteId: ID
    $title: String
    $category: AccountReviewNoteCategoryTypeEnum
  ) {
    createAccountReviewNote(
      accountReviewWorkflowId: $accountReviewWorkflowId
      note: $note
      accountReviewNoteId: $accountReviewNoteId
      title: $title
      category: $category
    ) {
      status
      message
    }
  }
`;

export type RpmAccountReviewWorkflowNotesResponse = {
  rpmAccountReviewWorkflowNotes: {
    rpmAccountReviewNotes: PatientReviewNoteType[];
  };
};

export const RPM_ACCOUNT_REVIEW_WORKFLOW_NOTES = gql`
  subscription RpmAccountReviewWorkflowNotes($accountReviewWorkflowId: ID!) {
    rpmAccountReviewWorkflowNotes(accountReviewWorkflowId: $accountReviewWorkflowId) {
      rpmAccountReviewNotes {
        id
        note
        authorType
        noteCategory
        relyingPartyAdmin {
          id
          presignedAvatarUrl
          user {
            firstName
            lastName
          }
        }
        replies {
          id
          note
          authorType
          relyingPartyAdmin {
            id
            presignedAvatarUrl
            user {
              firstName
              lastName
            }
          }
          createdAt
        }
        accountsFollowupReminders {
          id
          relyingPartyAdmin {
            id
            user {
              firstName
              lastName
            }
            presignedAvatarUrl
          }
          createdByRelyingPartyAdmin {
            id
            user {
              firstName
              lastName
            }
          }
          followUpOn
          notes
          type
          status
          createdAt
        }
        createdAt
      }
    }
  }
`;

export const DELETE_REVIEW_NOTE = gql`
  mutation DeleteAccountReviewNote($accountReviewNoteId: ID!) {
    deleteAccountReviewNote(accountReviewNoteId: $accountReviewNoteId) {
      status
      message
    }
  }
`;

export const UPDATE_ACCOUNT_REVIEW_WORKFLOW_STATUS = gql`
  mutation UpdateAccountReviewWorkflowStatus($accountReviewWorkflowId: ID!, $reviewType: String!) {
    updateAccountReviewWorkflowStatus(accountReviewWorkflowId: $accountReviewWorkflowId, reviewType: $reviewType) {
      status
      message
    }
  }
`;

export const UPDATE_ACCOUNT_REVIEW_NOTE = gql`
  mutation UpdateAccountReviewNote($accountReviewNoteId: ID!, $note: String!, $title: String) {
    updateAccountReviewNote(accountReviewNoteId: $accountReviewNoteId, note: $note, title: $title) {
      status
      message
    }
  }
`;

export type RelyingPartyAdminFollowUpResponse = {
  relyingPartyFollowups: { accountFollowups: FollowUpRemindersTypes[]; meta?: MetaDataType };
};

export const CURRENT_RELYING_PARTY_ADMIN_FOLLOWUP_ID = gql`
  query RelyingPartyFollowupsQuery($pageNum: Int, $pageSize: Int, $filterOptions: AccountFollowupFilterOptionsInput) {
    relyingPartyFollowups(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accountFollowups {
        id
      }
    }
  }
`;

export const CURRENT_RELYING_PARTY_FOLLOWUPS = gql`
  query RelyingPartyFollowupsQuery($pageNum: Int, $pageSize: Int, $filterOptions: AccountFollowupFilterOptionsInput) {
    relyingPartyFollowups(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accountFollowups {
        id
        followUpOn
        type
        mode
        appointmentRemindersEnabled
        subType
        status
        notes
        immediateActions
        account {
          truentityId
          user {
            firstName
            lastName
          }
          currentAccountsAssignments {
            relyingPartyAdmin {
              id
              name
            }
          }
          phone
        }
        category
        rpmAlert {
          label
        }
        rpmAccountReviewNote {
          rpmAccountReviewWorkflow {
            rpmReport {
              monthYear
            }
          }
        }
        relyingPartyAdmin {
          id
          name
          roleType
          user {
            firstName
            lastName
          }
        }
        rpmFollowupConsult {
          id
          consultAt
        }
        clinicalCareEncounter {
          id
          startedAt
        }
        createdByRelyingPartyAdmin {
          user {
            firstName
            lastName
          }
        }
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export const CURRENT_RELYING_PARTY_FOLLOWUPS_FOR_CALENDAR = gql`
  query RelyingPartyFollowupsQuery($pageNum: Int, $pageSize: Int, $filterOptions: AccountFollowupFilterOptionsInput) {
    relyingPartyFollowups(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accountFollowups {
        id
        followUpOn
        type
        mode
        appointmentRemindersEnabled
        subType
        status
        notes
        account {
          truentityId
        }
        category

        relyingPartyAdmin {
          id
          user {
            firstName
            lastName
          }
        }
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export const CURRENT_RELYING_PARTY_FOLLOWUPS_ACCOUNTS = gql`
  query RelyingPartyFollowupsQuery($pageNum: Int, $pageSize: Int, $filterOptions: AccountFollowupFilterOptionsInput) {
    relyingPartyFollowups(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accountFollowups {
        account {
          id
          truentityId
          user {
            firstName
            lastName
          }
          rpmStatus
          mtmStatus
          tcmStatus
          phone
          birthDate
          tags {
            id
            name
            colorCode
          }
        }
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export type GetRpmMonthlyReviewAccountsResponse = {
  getMonthlyReviewRpmReports: {
    reports: RpmReportsType[];
    meta: MetaDataType;
  };
};

export const GET_MONTHLY_REVIEW_RPM_REPORTS = gql`
  query GetMonthlyReviewRpmReportsQuery($filterOptions: RpmReportMonthlyReviewInput!, $pageNum: Int, $pageSize: Int, $monthYear: String!) {
    getMonthlyReviewRpmReports(filterOptions: $filterOptions, pageNum: $pageNum, pageSize: $pageSize) {
      reports {
        id
        monthYear
        account {
          id
          truentityId
          user {
            firstName
            lastName
          }
          birthDate
          healthPlan {
            orgName
          }
          criticalAlertCount(monthYear: $monthYear)
        }
        rpmAccountReviewWorkflow {
          signOffAt
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type PatientReviewTemplateResponse = {
  getPatientReviewReportTemplates: PatientReviewTemplateType[];
};

export const GET_PATIENT_REVIEW_TEMPLATES = gql`
  query GetPatientReviewReportTemplates {
    getPatientReviewReportTemplates {
      category
      templates {
        templateString
        dynamicFields
      }
    }
  }
`;

export type GetRpmMonthlyReviewReportsCountsResponse = {
  getMonthlyReviewRpmReportsCounts: {
    pendingCount: number;
    inProgressCount: number;
    signedOffCount: number;
  };
};

export const GET_MONTHLY_REVIEW_RPM_REPORTS_COUNTS = gql`
  query GetMonthlyReviewRpmReportsCountsQuery($filterOptions: RpmReportMonthlyReviewInput!) {
    getMonthlyReviewRpmReportsCounts(filterOptions: $filterOptions) {
      pendingCount
      inProgressCount
      signedOffCount
    }
  }
`;

export type GetAllRpmChartReviewsResponse = {
  getAllRpmChartReviews: {
    reports: RpmReportsType[];
    meta: MetaDataType;
  };
};

export const GET_ALL_RPM_CHART_REVIEWS_QUERY = gql`
  query GetAllRpmChartReviewsQuery($filterOptions: RpmReportMonthlyReviewInput!, $pageNum: Int, $pageSize: Int, $monthYear: String!) {
    getAllRpmChartReviews(filterOptions: $filterOptions, pageNum: $pageNum, pageSize: $pageSize) {
      reports {
        id
        account {
          truentityId
          user {
            firstName
            lastName
            middleName
          }
          criticalAlertCount(monthYear: $monthYear)
        }
        monthYear
        rpmAccountReviewWorkflow {
          signOffAt
          provider {
            id
            nameWithTitle
          }
          careActivity {
            totalTimeSpentSecs
          }
        }
        relyingParty {
          name
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetAllRpmChartReviewProvidersResponse = {
  getAllRpmChartReviewProviders: {
    providers: {
      id: string;
      nameWithTitle: string;
    }[];
  };
};

export const GET_ALL_RPM_CHART_REVIEW_PROVIDERS = gql`
  query GetAllRpmChartReviewProviders {
    getAllRpmChartReviewProviders {
      providers {
        id
        nameWithTitle
      }
    }
  }
`;

export type RpmProviderSignoffRowType = {
  providerFirstName: string | null;
  providerLastName: string | null;
  signOffDate: string | null;
  totalSignoffs: number;
  encounterType: string | null;
  encounterMode: string | null;
};

export type GetRpmProviderSignoffsResponse = {
  getRpmProviderSignoffs: {
    rows: RpmProviderSignoffRowType[];
    meta: MetaDataType;
  };
};

export const GET_RPM_PROVIDER_SIGNOFFS_QUERY = gql`
  query GetRpmProviderSignoffsQuery($filterOptions: RpmProviderSignoffInput!) {
    getRpmProviderSignoffs(filterOptions: $filterOptions) {
      rows {
        providerFirstName
        providerLastName
        signOffDate
        totalSignoffs
        encounterType
        encounterMode
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetAllRpmProviderSignoffProvidersResponse = {
  getAllRpmProviderSignoffProviders: {
    providers: {
      id: string;
      nameWithTitle: string;
    }[];
  };
};

export const GET_ALL_RPM_PROVIDER_SIGNOFF_PROVIDERS = gql`
  query GetAllRpmProviderSignoffProviders {
    getAllRpmProviderSignoffProviders {
      providers {
        id
        nameWithTitle
      }
    }
  }
`;

export type AccountHandoutResponse = {
  getAccountHandout: {
    handoutS3Key: string;
  };
};

export const GET_ACCOUNT_HANDOUT_PDF = gql`
  query GetAccountHandout {
    getAccountHandout {
      handoutS3Key
    }
  }
`;

export type ArchiveRpmAlertsResponse = {
  archiveRpmAlerts: {
    status: string;
    message: string;
  };
};

export const ARCHIVE_RPM_ALERTS = gql`
  mutation ArchiveRpmAlerts($monthYear: String!) {
    archiveRpmAlerts(monthYear: $monthYear) {
      status
      message
    }
  }
`;

export type TogglePriorityCandidateSnoozeResponse = {
  togglePriorityPatientSnooze: {
    status: string;
    message: string;
  };
};

export const TOGGLE_PRIOTITY_CANDIDATE_SNOOZE = gql`
  mutation TogglePriorityPatientSnooze($truentityIds: [ID!]!, $isSnoozed: Boolean!) {
    togglePriorityPatientSnooze(truentityIds: $truentityIds, isSnoozed: $isSnoozed) {
      status
      message
    }
  }
`;

export type RecalculatedAccountMetric = AccountMetric & {
  id: string;
  accountId: string;
  hasDiabetes: boolean | null;
  hasHypertension: boolean | null;
  createdAt: string;
  updatedAt: string;
};

export type RecalculatePriorityScoreResponse = {
  recalculatePriorityScore: {
    accountMetric: RecalculatedAccountMetric;
  };
};

export const RECALCULATE_PRIORITY_SCORE = gql`
  mutation RecalculatePriorityScore($truentityId: String!) {
    recalculatePriorityScore(truentityId: $truentityId) {
      accountMetric {
        id
        accountId
        rpmPriorityScore
        isSnoozed
        hasDiabetes
        hasHypertension
        diabetesLikelihoodScore
        hypertensionLikelihoodScore
        medicationData
        claimsData
        diagnosesData
        medsyncData
        metaData
        createdAt
        updatedAt
      }
    }
  }
`;

export type BatchPriorityScoreStartResult = {
  status: 'queued' | 'in_progress';
  message: string;
  relyingPartyId: string | null;
  jobId: string | null;
};

export type BatchPriorityScoreStatus = {
  status: 'idle' | 'queued' | 'processing' | 'completed' | 'failed';
  total?: string;
  processed?: string;
  succeeded?: string;
  failed?: string;
  jobId?: string;
  startedAt?: string;
  updatedAt?: string;
  completedAt?: string;
  error?: string;
  failedAt?: string;
};

export type CalculateRpPriorityScoresResponse = {
  calculateRelyingPartyPriorityScores: BatchPriorityScoreStartResult;
};

export type RpPriorityScoreBatchStatusResponse = {
  // JSON scalar — all numeric fields arrive as strings (e.g. "47")
  getRelyingPartyPriorityScoreBatchStatus: BatchPriorityScoreStatus;
};

export const CALCULATE_RP_PRIORITY_SCORES = gql`
  mutation StartBatchScoring($relyingPartyId: ID!) {
    calculateRelyingPartyPriorityScores(relyingPartyId: $relyingPartyId) {
      status
      message
      relyingPartyId
      jobId
    }
  }
`;

export const GET_RP_PRIORITY_SCORE_BATCH_STATUS = gql`
  query BatchScoringStatus($relyingPartyId: ID!) {
    getRelyingPartyPriorityScoreBatchStatus(relyingPartyId: $relyingPartyId)
  }
`;

export type GetFollowUpVisitStatusCountsResponse = {
  getRpmEnrolledAccountsFilters: {
    visitFilters: EnrolledAccountsFilterType[];
    monthlyTaskFilters: EnrolledAccountsFilterType[];
    billingFilters?: EnrolledAccountsFilterType[];
  };
};

export const GET_RPM_ENROLLED_ACCOUNTS_FILTERS = gql`
  query GetRpmEnrolledAccountsFiltersQuery($filterMonthYear: String!, $filterByAccountIds: [ID!], $includeCcmEnrolled: Boolean) {
    getRpmEnrolledAccountsFilters(
      filterMonthYear: $filterMonthYear
      filterByAccountIds: $filterByAccountIds
      includeCcmEnrolled: $includeCcmEnrolled
    ) {
      visitFilters {
        status
        count
      }
      monthlyTaskFilters {
        status
        count
      }
      billingFilters {
        status
        count
      }
    }
  }
`;

export type GetRpmFollowupConsultVisitReportsType = {
  getRpmFollowupConsult: {
    account: {
      lastAvailableAccountCareSummary: {
        s3Key: string;
      };
    };
    reportS3Key: string;
    combinedReportS3Key: string;
  };
};

export const GET_RPM_FOLLOWUP_CONSULT_VISIT_REPORTS = gql`
  query GetRpmFollowupConsultQuery($followupConsultId: String!) {
    getRpmFollowupConsult(followupConsultId: $followupConsultId) {
      account {
        lastAvailableAccountCareSummary {
          s3Key
        }
      }
      reportS3Key
      combinedReportS3Key
    }
  }
`;

export type RemoveAccountMonitoringDevicesResponseType = {
  removeAccountMonitoringDevices: {
    status: string;
    message: string;
  };
};

export const UNLINK_PATIENT_MONITORING_DEVICES = gql`
  mutation RemoveAccountMonitoringDevices($truentityId: ID!) {
    removeAccountMonitoringDevices(truentityId: $truentityId) {
      status
      message
    }
  }
`;

export type GetAccountMedicalServiceEnrollmentSummaryResponse = {
  getAccountMedicalServiceEnrollmentSummary: AccountMedicalServiceEnrollmentSummary;
};

export const GET_ACCOUNT_MEDICAL_SERVICE_ENROLLMENT_SUMMARY = gql`
  query GetAccountMedicalServiceEnrollmentSummary($truentityId: ID!, $reviewDatetime: ISO8601DateTime!) {
    getAccountMedicalServiceEnrollmentSummary(truentityId: $truentityId, reviewDatetime: $reviewDatetime) {
      rpmEnrolled
      ccmEnrolled
      ccmBillable
      rpmStatus
      ccmStatus
      rpmEnrolledAt
      rpmUnenrolledAt
      ccmEnrolledAt
      ccmUnenrolledAt
      rpmMonthToDateSecs
      ccmMonthToDateSecs
      monthYear
    }
  }
`;

export const GET_PROVIDER_WORKQUEUE = gql`
  query GetProviderWorkqueue(
    $workqueueType: String
    $filterOptions: ProviderWorkqueueFilterOptionsInput
    $pageNum: Int
    $pageSize: Int
    $sortField: String
    $sortDirection: String
  ) {
    getProviderWorkqueue(
      workqueueType: $workqueueType
      filterOptions: $filterOptions
      pageNum: $pageNum
      pageSize: $pageSize
      sortField: $sortField
      sortDirection: $sortDirection
    ) {
      workqueueItems {
        workqueueStatus
        truentityId
        patientName
        pharmacyName
        visitType
        rpmFollowupConsultId
        clinicalCareEncounterId
        signedOffAt
        lastAmendedAt
        rpmEnrolledAt
        ccmEnrolledAt
        currentEnrolledPrograms
        noteVersion
        provider
        amendingProvider
        providerSignedOffAt
      }
      summary {
        incompleteCount
        totalCount
      }
      renderingProviders {
        id
        name
      }
      currentProvider {
        id
        name
      }
      meta {
        currentPage
        perPage
        prevPage
        nextPage
        totalCount
        totalPages
      }
    }
  }
`;

export type GetProviderWorkqueueResponse = {
  getProviderWorkqueue: {
    workqueueItems: ProviderWorkqueueItemType[];
    summary: ProviderWorkqueueSummaryType;
    renderingProviders: ProviderWorkqueueProviderType[];
    // The logged-in admin's own provider record, or null when they have none (super admins).
    currentProvider?: ProviderWorkqueueProviderType | null;
    meta: {
      currentPage: number;
      perPage: number;
      prevPage?: number | null;
      nextPage?: number | null;
      totalCount: number;
      totalPages: number;
    };
  } | null;
};
